'use client';

import { useOnClickOutside } from 'usehooks-ts';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useDebounce } from '@uidotdev/usehooks';
import classNames from 'classnames';
import { PRODUCT_QUERY_PARAMS } from '@/features/navigation/constants';
import { IconProgressActivity } from '@/features/icons';
import { IconClose, IconSearchNormal } from '@/components/svg-symbols/icons';
import { useUserToken } from '@/app/_utils/session';
import { MIN_ACCEPTABLE_QUERY_LENGTH } from './constants';
import Backdrop from './backdrop';
import LastSearchHistory from './last-search-history';
import SearchResult from './search-result';
import Suggestions from './suggestions';
import { SuggestionType } from './types';
import fetchSearchResult from './utils/fetch-search-results';
import { generateSearchUrl } from './utils/search-helpers';
import useSearchHistory from './utils/use-search-history';
import SearchSkeleton from '../components/search-skeleton';
interface Props {
  mode: 'mobile' | 'desktop';
}
const SearchBox = forwardRef<HTMLInputElement, Props>(function SearchBox({
  mode = 'desktop'
}, inputRef) {
  const formRef = useRef(null);
  const [ut] = useUserToken();
  const searchParams = useSearchParams();
  const queryParam = searchParams.get(PRODUCT_QUERY_PARAMS.SEARCH);
  const [query, setQuery] = useState<string>('');
  const debouncedQuery = useDebounce(query, 300);
  const [historyItems, addHistoryItem] = useSearchHistory();
  const [isFocus, setIsFocus] = useState<boolean>(false);
  const isMobile = mode === 'mobile';
  const pathname = usePathname();
  const handleInputFocus = () => {
    try {
      // @ts-expect-error: Goftino doesn't include types
      Goftino.close();
    } catch (error) {
      // nothing
    }
    return setIsFocus(true);
  };

  // check if route changed, force close the search resualt container
  useEffect(() => {
    setIsFocus(false);
  }, [pathname]);
  useOnClickOutside(formRef, () => {
    if (isMobile) return;
    setIsFocus(false);
  });
  const {
    isLoading,
    isError,
    data: response,
    isFetching
  } = useQuery({
    queryKey: ['search', {
      ut,
      debouncedQuery
    }],
    queryFn: ({
      signal
    }) => fetchSearchResult({
      ut,
      query: debouncedQuery,
      signal
    }),
    staleTime: 10 * 60 * 1000,
    enabled: (debouncedQuery.length === 0 || debouncedQuery.length > MIN_ACCEPTABLE_QUERY_LENGTH) && !!ut,
    placeholderData: keepPreviousData
  });
  const results = response && response.isSuccess ? response.data : null;
  const noResult = !results?.brands?.length && !results?.categories?.length && !results?.products?.length && !(isFetching && isLoading);
  const queryId = results?.queryId;
  const handleSubmit = (e: React.FormEvent) => {
    if (!query.length) {
      e.preventDefault();
      if (mode === 'mobile') return;
      setIsFocus(false);
      if (inputRef && typeof inputRef === 'object' && 'current' in inputRef) {
        inputRef.current?.blur();
      }
      return false;
    }
    setQuery(query.trim());
    addHistoryItem(query);
    return true;
  };
  useEffect(() => {
    setIsFocus(isMobile);
  }, [mode, setIsFocus, isMobile]);
  useEffect(() => {
    setQuery(queryParam || '');
    setIsFocus(false);
  }, [queryParam]);
  return <>
            <form className={classNames('relative z-top-of-chatbot-plus3 mx-auto max-w-xl grow flex-wrap rounded border-none lg:flex', isMobile ? 'flex' : 'hidden')} autoComplete='off' ref={formRef} data-test='search-form' onSubmit={handleSubmit} action={generateSearchUrl()}>
                <div className='relative flex w-full overflow-hidden rounded'>
                    <div className='absolute right-0 top-0 flex h-full items-center justify-center rounded px-1.5'>
                        {isFetching && query.length ? <IconProgressActivity className='mr-1 h-6 w-6 animate-spin text-text-mediumGray' /> : <>
                                <IconSearchNormal className={classNames('mr-1 h-6 w-6 text-text-mediumGray', {
              hidden: query.length
            })} />
                                <button type='button' onClick={() => setQuery('')} data-test='clear-result' className={classNames('flex text-text-mediumGray', {
              hidden: !query.length
            })}>
                                    <IconClose className='mr-1 h-6 w-6' />
                                </button>
                            </>}
                    </div>

                    <input ref={inputRef} value={query} type='text' autoComplete='off' onFocus={handleInputFocus} placeholder='جست‌وجو' className='h-11 flex-1 rounded border border-border-gray-medium py-2.5 pl-2 pr-10 text-body-14 leading-relaxed outline-none placeholder:text-text-mediumGray focus:border-pink-200 md:min-w-[520px] lg:text-body-16' onChange={e => setQuery(e.target.value.trimStart())} name='query' />
                </div>

                <div data-test='search-result' className={classNames('z-10 w-full rounded py-2 lg:absolute lg:right-0 lg:top-full lg:mr-0 lg:mt-1 lg:overflow-y-auto lg:border lg:border-border-gray-medium lg:bg-white lg:px-1 lg:shadow-md', isFocus ? 'flex' : 'hidden')}>
                    {isFocus && <div className='relative w-full leading-4 lg:mt-0'>
                            {isFetching && query.length ? <SearchSkeleton className='lg:mt-2' /> : <div className='mt-2'>
                                    <span data-test='search-result--empty' className={classNames('mb-18 mt-2 text-body-14 text-text-mediumGray lg:mb-7 lg:mt-4 lg:px-1 lg:text-body-16', query.length > MIN_ACCEPTABLE_QUERY_LENGTH && noResult && !isError ? 'block' : 'hidden')}>
                                        برای جست‌وجوی شما نتیجه‌ای یافته نشد!
                                    </span>

                                    {isError && query.length && <span data-test='search-result--error' className='mb-2 mr-6 text-body-14 lg:px-4 lg:text-body-16'>
                                            جست‌وجو با خطا مواجه شد.
                                        </span>}

                                    {results?.categories && <SearchResult dataTest='categories' queryId={queryId} items={results.categories} type='دسته' />}

                                    {results?.brands && <SearchResult queryId={queryId} dataTest='brands' items={results.brands} type='برند' />}

                                    {!query.length && !results?.queryId && historyItems && <LastSearchHistory items={historyItems} />}

                                    <Suggestions dataTest='popular' type={SuggestionType.Popular} queryId={queryId} perView='auto' items={results?.popularSearch || null} title='جست‌وجوهای پرطرفدار' />

                                    <Suggestions dataTest='products' type={SuggestionType.Products} queryId={queryId} perView={2.3} items={results?.products || null} title='جست‌وجو در میان محصولات' />
                                </div>}
                        </div>}
                </div>
            </form>
            {isFocus && <Backdrop />}
        </>;
});
export default SearchBox;